import {
  getUserRoleLabel,
  type User,
  type UserRole,
} from "@employee-management/common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit,
  Key,
  MoreHorizontal,
  Trash2,
  UserMinus,
  UserPlus,
  User as UserIcon,
  Calendar,
  Shield,
  Activity,
  ShieldUser,
} from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiService } from "@/lib/api-service";
import { filterData } from "@/lib/filter-utils";
import { FilterSettings } from "@/components/ui/search-filter-bar";
import { UserDeleteDialog } from "../UserDeleteDialog/UserDeleteDialog";
import { UserEditDialog } from "../UserEditDialog/UserEditDialog";
import { UserResetPasswordDialog } from "../UserResetPasswordDialog/UserResetPasswordDialog";

interface UserTableProps {
  filterSettings?: FilterSettings;
}

export const UserTable = ({ filterSettings }: UserTableProps) => {
  const {
    data: rawData = [],
    isLoading,
    isError,
  } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => apiService.get<User[]>("/users"),
  });

  // Filter data based on filterSettings
  const data = useMemo(() => {
    if (!filterSettings) return rawData;
    return filterData(rawData, filterSettings, ["name", "email"]);
  }, [rawData, filterSettings]);

  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);

  // State for dialogs - using a map to track dialog states for each user
  const [dialogStates, setDialogStates] = useState<
    Record<
      string,
      {
        editOpen: boolean;
        deleteOpen: boolean;
        resetPasswordOpen: boolean;
      }
    >
  >({});

  const setDialogState = (
    userId: string,
    dialogType: keyof (typeof dialogStates)[string],
    value: boolean
  ) => {
    setDialogStates((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [dialogType]: value,
      },
    }));
  };

  const toggleStatusMutation = useMutation({
    mutationFn: async ({
      userId,
      isActive,
    }: {
      userId: string;
      isActive: boolean;
    }) => {
      await apiService.put(`/users/${userId}`, {
        isActive: !isActive,
      });
    },
    onSuccess: (_, { userId: _userId, isActive }) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(
        `User ${isActive ? "deactivated" : "activated"} successfully`
      );
    },
    onError: () => {
      toast.error("Failed to update user status");
    },
  });

  const handleToggleStatus = (user: User) => {
    if (!user.id) return;
    toggleStatusMutation.mutate({ userId: user.id, isActive: user.isActive });
  };

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <div className="w-[25ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <UserIcon className="h-4 w-4" />
            Name
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="font-medium flex items-center gap-3">
          <div className="flex items-center justify-center h-10 w-10 bg-muted rounded-lg">
            <ShieldUser className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex flex-col gap-1">
            <p>{row.getValue("name")}</p>
            <p className="text-xs text-muted-foreground">
              {row.original.email}
            </p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "role",
      header: ({ column }) => (
        <div className="w-[12ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Shield className="h-4 w-4" />
            Role
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const role = row.getValue("role") as UserRole;
        return (
          <Badge variant="outline">{role && getUserRoleLabel(role)}</Badge>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: ({ column }) => (
        <div className="w-[12ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Activity className="h-4 w-4" />
            Status
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <Badge variant={row.getValue("isActive") ? "default" : "secondary"}>
          {row.getValue("isActive") ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <div className="w-[12ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Calendar className="h-4 w-4" />
            Created
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const date = row.getValue("createdAt") as string;
        return date ? new Date(date).toLocaleDateString() : "N/A";
      },
    },
    {
      id: "actions",
      header: " ",
      cell: ({ row }) => {
        const user = row.original;
        const userDialogState = dialogStates[user.id || ""] || {
          editOpen: false,
          deleteOpen: false,
          resetPasswordOpen: false,
        };

        return (
          <>
            <div className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() =>
                      user.id && setDialogState(user.id, "editOpen", true)
                    }
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit User
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      user.id &&
                      setDialogState(user.id, "resetPasswordOpen", true)
                    }
                  >
                    <Key className="mr-2 h-4 w-4" />
                    Reset Password
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                    {user.isActive ? (
                      <>
                        <UserMinus className="mr-2 h-4 w-4" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Activate
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      user.id && setDialogState(user.id, "deleteOpen", true)
                    }
                    className="text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete User
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Dialogs for this user */}
            <UserEditDialog
              open={userDialogState.editOpen}
              onOpenChange={(open) =>
                user.id && setDialogState(user.id, "editOpen", open)
              }
              user={user}
            />
            <UserDeleteDialog
              open={userDialogState.deleteOpen}
              onOpenChange={(open) =>
                user.id && setDialogState(user.id, "deleteOpen", open)
              }
              user={user}
            />
            <UserResetPasswordDialog
              open={userDialogState.resetPasswordOpen}
              onOpenChange={(open) =>
                user.id && setDialogState(user.id, "resetPasswordOpen", open)
              }
              user={user}
            />
          </>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div className="text-red-500">Failed to load users.</div>;

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="!bg-muted/40">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className="hover:bg-transparent"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No users found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
